import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { reduxForm, getFormValues, reset } from 'redux-form';
import TenantServiceHeader from '../../components/bulkPeriodManagement/TenantServiceHeader';
import * as tenantServiceActions from '../../actions/tenantServiceActions';
import { withRouter } from '../../common/withRouter';
import { MotifTabNavigation, MotifTabControl } from '@ey-xd/motif-react';
import { Link } from 'react-router';
import * as periodActions from '../../actions/periodActions';
import * as trsActions from '../../actions/trsActions';
import TenantServicesTable from '../../components/bulkPeriodManagement/TenantServicesTable';
import * as amenities from '../../constants/amenities';

import UploadTenantServiceModal from '../../components/bulkPeriodManagement/UploadTenantServiceModal';
/**
 * TenantServicesContainer component
 * @extends {React.Component}
 */
export class TenantServicesContainer extends React.Component {
  /**
   * Creates a new TenantServicesContainer
   * @constructor
   * @param {Object} props The component properties
   * @param {Object} context The component context
   */
  constructor(props, context) {
    super(props, context);
    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleGridChanges = this.handleGridChanges.bind(this);
    this.handleGridColumnStateChange =
      this.handleGridColumnStateChange.bind(this);
    this.handleEditPropertiesTenantServices =
      this.handleEditPropertiesTenantServices.bind(this);
    this.toggleGridEditMode = this.toggleGridEditMode.bind(this);
    this.handleAddTenantService = this.handleAddTenantService.bind(this);
    this.handleDeleteTenantService = this.handleDeleteTenantService.bind(this);
    this.handleUploadPropertiesTenantServices =
      this.handleUploadPropertiesTenantServices.bind(this);
    this.handleExportPropertiesTenantServices =
      this.handleExportPropertiesTenantServices.bind(this);
    this.state = {
      defaultReportPeriod: null,
      gridCurrentState: [],
      gridColumnState: [],
      tenantServicesData: [],
      isActionsDisabled: false,
      isEditModeActive: false,
      gridDataAfterAction: [],
      deleteTenantServiceList: [],
      // This is used to reset the updated grid data(used to track changes) after save action is completed
      resetUpdatedRowDataTrigger: 0,
      showUploadTenantServicesModal: false,
      selectedReportPeriod: null,
    };

    // This is used to reset to original data when cancel button is clicked
    this.originalTenantServicesData = [];
  }

  /**
   * Invoked immediately after mounting occurs
   * @returns {undefined}
   */
  componentDidMount() {
    this.fetchReportPeriodsByClientId();
  }

  /**
   * Invoked when a new props is recived
   * @returns {undefined}
   */
  componentDidUpdate(prevProps) {
    // Check if tenantServiceFormValues have changed
    if (
      prevProps.tenantServiceFormValues !==
        this.props.tenantServiceFormValues &&
      this.props.tenantServiceFormValues?.reportPeriods?.length > 0
    ) {
      const { reportPeriods } = this.props.tenantServiceFormValues;
      this.setState({ selectedReportPeriod: reportPeriods });
      this.fetchTenantServiceData(this.props.clientId, reportPeriods);

      // Fetch TRS data for the client and report period
      this.props.trsActions.fetchTRSDataByClientIdAndReportPeriod(
        this.props.clientId,
        reportPeriods
      );
    }
  }

  fetchTenantServiceData(clientId, reportPeriodID) {
    this.props.tenantServiceActions
      .fetchTenantServiceData(clientId, reportPeriodID)
      .then((tenantServicesData) => {
        const isActionsDisabled =
          !tenantServicesData ||
          tenantServicesData.length === 0 ||
          tenantServicesData.every((row) => row.propertyTrackingID === null);

        this.setState({
          tenantServicesData: tenantServicesData || [],
          isActionsDisabled,
          isEditModeActive: false,
        });
      });
  }

  handleExportPropertiesTenantServices() {
    const { clientId } = this.props;
    this.props.tenantServiceActions.exportTenantServicesData(
      clientId,
      this.state.selectedReportPeriod
    );
  }

  handleUploadPropertiesTenantServices() {
    this.setState({ showUploadTenantServicesModal: true });
  }

  toggleUploadTenantServicesModal = () => {
    this.setState({
      showUploadTenantServicesModal: !this.state.showUploadTenantServicesModal,
    });
    this.fetchTenantServiceData(
      this.props.clientId,
      this.state.selectedReportPeriod
    );
  };

  fetchReportPeriodsByClientId() {
    this.props.periodActions
      .fetchReportPeriodsByClientId(this.props.clientId)
      .then((periods) => {
        let defaultReportPeriod = null;
        if (periods && periods.length > 0) {
          const currentDate = new Date();
          const currentQuarter = `Q${Math.ceil((currentDate.getMonth() + 1) / 3)} ${currentDate.getFullYear()}`;

          // Parse endDate once for each period
          const periodsWithParsedDate = periods.map((rp) => ({
            ...rp,
            parsedEndDate: new Date(rp.endDate),
          }));

          // Sort periods in descending order by parsedEndDate
          const periodsInSortedOrder = [...periodsWithParsedDate].sort(
            (a, b) => b.parsedEndDate - a.parsedEndDate
          );

          // Find the max report period
          const maxReportPeriod = periodsInSortedOrder[0];

          // Check if the current quarter exists in periods
          const currentQuarterPeriod = periodsInSortedOrder.find(
            (rp) => rp.reportPeriodDescription === currentQuarter
          );

          if (currentQuarterPeriod) {
            defaultReportPeriod = currentQuarterPeriod.reportPeriodDescription;
          } else {
            // Find the nearest future period (parsedEndDate > today), sorted ascending
            const futurePeriods = periodsWithParsedDate
              .filter((rp) => rp.parsedEndDate > currentDate)
              .sort((a, b) => a.parsedEndDate - b.parsedEndDate);
            if (futurePeriods.length > 0) {
              defaultReportPeriod = futurePeriods[0].reportPeriodDescription;
            } else {
              // Fallback to the latest available period
              defaultReportPeriod = maxReportPeriod.reportPeriodDescription;
            }
          }

          this.setState({
            defaultReportPeriod,
            selectedReportPeriod: defaultReportPeriod,
          });
        }
        this.fetchTenantServiceData(this.props.clientId, defaultReportPeriod);
        // Fetch TRS data for the client and report period
        this.props.trsActions.fetchTRSDataByClientIdAndReportPeriod(
          this.props.clientId,
          defaultReportPeriod
        );
      });
  }

  setActiveTab(currentIndex) {
    this.setState({ tabBar1: currentIndex });
  }

  handleGridChanges(gridRowData) {
    let rowData = [];
    rowData =
      gridRowData &&
      gridRowData.length &&
      gridRowData.map((x) => {
        return { ...x };
      });
    this.setState({ gridCurrentState: rowData });
  }

  handleGridColumnStateChange(columnState) {
    this.setState({ gridColumnState: columnState });
  }
  //Set current tab index based
  handleTabClick(index) {
    this.setActiveTab(index);
  }

  /**
   * Activates edit mode for tenant services .
   * This method updates the component's state by setting `isEditModeActive` to `true`,
   * allowing for modifications to the tenant services.
   */
  handleEditPropertiesTenantServices() {
    // Cache the original tenant services data before any edits
    this.originalTenantServicesData = JSON.parse(
      JSON.stringify(this.props.tenantServicesData)
    );

    this.setState({ isEditModeActive: true });
  }

  /**
   * Toggles the edit mode for the grid.
   * This method updates the component's state by switching the `isEditModeActive` value
   * between `true` and `false`, enabling or disabling edit functionality dynamically.
   */
  toggleGridEditMode(action, gridApi) {
    if (action === 'save') {
      const displayedData = [];
      if (gridApi) {
        gridApi.forEachNode((node) => {
          displayedData.push(node.data); // Collect displayed row data
        });
      }

      // Filter out rows that are completely empty and not marked for deletion
      const filteredData = displayedData.filter((item) => {
        // Skip items already marked for deletion
        if (item.deleteCommand === true) {
          return true;
        }

        const isEmptyService = !item.propertyAmenityName;
        const allFlagsFalse =
          !item.isProvidedByCustomary &&
          !item.isProvidedByEmployee &&
          !item.isProvidedByIndependentContractor &&
          !item.isProvidedByTaxableReitSubsidiary;

        // If the item is new (propertyAmenityID < 0) and has no service name or all flags are false, skip it
        if (
          (item.propertyAmenityID == null || item.propertyAmenityID < 0) &&
          isEmptyService &&
          allFlagsFalse
        ) {
          return false;
        }

        // If the item is an existing service (propertyAmenityID > 0) and has no service name or all flags are false, mark it for deletion
        if (item.propertyAmenityID > 0 && isEmptyService && allFlagsFalse) {
          item.deleteCommand = true;
        }

        return true;
      });

      // SKIP: If no rows are left after filtering, and also no rows are marked for deletion, do not proceed with save
      if (
        filteredData.length === 0 &&
        this.state.deleteTenantServiceList.length === 0
      ) {
        this.setState({
          isEditModeActive: false,
          gridDataAfterAction: [...this.originalTenantServicesData],
          resetUpdatedRowDataTrigger: Date.now(),
        });
        return;
      }

      const finalTenantServiceList = [
        ...filteredData,
        ...this.state.deleteTenantServiceList,
      ].map((item) => ({
        ...item,
        deleteCommand: Object.prototype.hasOwnProperty.call(
          item,
          'deleteCommand'
        )
          ? item.deleteCommand
          : false,
      }));

      const request = {
        ClientID: this.props.clientId,
        ManageTenantServiceDetail: finalTenantServiceList,
        ReportPeriodDescription: this.state.selectedReportPeriod,
      };

      // Save the Tenant Services data to the database
      this.props.tenantServiceActions.manageTenantServices(request).then(() => {
        this.setState({
          deleteTenantServiceList: [],
          isEditModeActive: false,
          resetUpdatedRowDataTrigger: Date.now(),
        });

        //Fetch the updated tenant services data
        this.fetchTenantServiceData(
          this.props.clientId,
          this.state.selectedReportPeriod
        );
      });
    } else if (action === 'cancel') {
      this.setState({
        deleteTenantServiceList: [],
        isEditModeActive: false,
        gridDataAfterAction: [...this.originalTenantServicesData],
        resetUpdatedRowDataTrigger: Date.now(),
      });
    }
  }

  /**
   * Adds a new tenant service row to the grid.
   * - Retrieves currently displayed rows from grid API.
   * - Finds the matching row by `uniqueKey`.
   * - Duplicates the row with a new negative `uniqueKey` to indicate a temporary addition.
   * - Enables `addMode` for the new row.
   * - Inserts the modified row after the original in the displayed data.
   * - Updates the component state with the updated grid data.
   */
  handleAddTenantService(gridRow, gridApi) {
    const displayedData = [];
    if (gridApi) {
      gridApi.forEachNode((node) => {
        displayedData.push(node.data); // Collect displayed row data
      });

      const index = displayedData.findIndex(
        (item) => item.uniqueKey === gridRow.uniqueKey
      );
      if (index !== -1) {
        const matchedRow = { ...displayedData[index] }; // Create a copy of the matched row
        const uniqueKey = (parseInt(Math.random() * (99999999 - 1)) + 1) * -1;
        matchedRow.propertyAmenityID = uniqueKey;
        matchedRow.addMode = true;
        matchedRow.isProvidedByCustomary = false;
        matchedRow.isProvidedByEmployee = false;
        matchedRow.isProvidedByIndependentContractor = false;
        matchedRow.isProvidedByTaxableReitSubsidiary = false;
        matchedRow.propertyAmenityName = '';
        matchedRow.propertyAmenityName_error = 'Please select Service Name.';
        matchedRow.serviceNameOtherDescription = '';
        matchedRow.serviceNameOtherDescription_error = '';
        matchedRow.trsName = '';
        matchedRow.taxableREITSubsidiaryID = -1;
        matchedRow.uniqueKey = uniqueKey;

        displayedData.splice(index + 1, 0, matchedRow);
        this.setState({ gridDataAfterAction: displayedData });
      }
    }
  }

  /**
   * Handles the deletion of a tenant service row from the grid.
   * - Retrieves the currently displayed rows from the grid API.
   * - Finds the matching row by `uniqueKey`.
   * - Removes the matched row from the displayed data array.
   * - Updates the component state to reflect the change in the grid.
   */
  handleDeleteTenantService(gridRow, gridApi) {
    const displayedData = [];

    if (gridApi) {
      // Collect displayed row data
      gridApi.forEachNode((node) => {
        displayedData.push(node.data);
      });

      // Find the index of the row to be deleted
      const index = displayedData.findIndex(
        (item) => item.uniqueKey === gridRow.uniqueKey
      );

      if (index !== -1) {
        const tenantServiceToBeDeleted = JSON.parse(
          JSON.stringify(displayedData[index])
        );

        // Remove the matching row from displayed data
        displayedData.splice(index, 1);

        // Check for remaining rows with the same PropertyID
        const stillExistsForProperty = displayedData.some(
          (row) => row.propertyID === tenantServiceToBeDeleted.propertyID
        );

        // If none remain, insert a blank placeholder at the same index
        if (!stillExistsForProperty) {
          const uniqueKey = (Math.floor(Math.random() * 99999999) + 1) * -1;

          const placeholderRow = {
            ...tenantServiceToBeDeleted, // retain structure
            propertyAmenityID: uniqueKey,
            addMode: true,
            isProvidedByCustomary: false,
            isProvidedByEmployee: false,
            isProvidedByIndependentContractor: false,
            isProvidedByTaxableReitSubsidiary: false,
            propertyAmenityName: '',
            propertyAmenityName_error: '',
            serviceNameOtherDescription_error: '',
            serviceNameOtherDescription: '',
            trsName: '',
            taxableREITSubsidiaryID: -1,
            uniqueKey: uniqueKey,
          };

          // Insert the new row exactly at the original index
          displayedData.splice(index, 0, placeholderRow);
        }

        // Update the state to reflect the deletion
        // Only insert existing db records which are deleted, to deleteTenantServiceList
        if (tenantServiceToBeDeleted.uniqueKey < 0) {
          this.setState({ gridDataAfterAction: displayedData });
        } else {
          this.setState((prevState) => ({
            gridDataAfterAction: displayedData,
            deleteTenantServiceList: prevState.deleteTenantServiceList.some(
              (item) => item.uniqueKey === tenantServiceToBeDeleted.uniqueKey
            )
              ? prevState.deleteTenantServiceList
              : [
                  ...prevState.deleteTenantServiceList,
                  { ...tenantServiceToBeDeleted, deleteCommand: true },
                ],
          }));
        }
      }
    }
  }

  /**
   * Render a React element
   * @returns {Object} A reference to the component
   */
  render() {
    const noData =
      !this.props.tenantServicesData ||
      this.props.tenantServicesData.length === 0;
    const noReit = !this.props.periods?.length;
    return (
      <>
        <h2 data-testid="pghdrBulkPeriodManagement">Bulk Period Management</h2>
        <MotifTabNavigation variant="default">
          <Link
            to={`/client/${this.props.clientId}/bulkPeriodManagement`}
            tabIndex="-1"
          >
            <MotifTabControl
              role="tab"
              data-testid="tbNewPeriod"
              onClick={() => this.handleTabClick(1)}
            >
              New Period
            </MotifTabControl>
          </Link>
          {/* Conditional rendering for Services Scope tab */}
          {!this.props.isAudit ? (
            <Link
              to={`/client/${this.props.clientId}/bulkPeriodManagement/servicesScope`}
              tabIndex="-1"
            >
              <MotifTabControl
                role="tab"
                data-testid="tbServicesScope"
                onClick={() => this.handleTabClick(2)}
              >
                Services Scope
              </MotifTabControl>
            </Link>
          ) : (
            <></>
          )}
          <Link
            to={`/client/${this.props.clientId}/bulkPeriodManagement/ownership`}
            tabIndex="-1"
          >
            <MotifTabControl
              role="tab"
              data-testid="tbOwnership"
              onClick={() => this.handleTabClick(3)}
            >
              Ownership %
            </MotifTabControl>
          </Link>
          <Link
            to={`/client/${this.props.clientId}/bulkPeriodManagement/tenantServicesAmenities`}
            tabIndex="-1"
          >
            <MotifTabControl
              className="motif-active"
              role="tab"
              data-testid="tbTenantServicesAmenities"
              onClick={() => this.handleTabClick(4)}
            >
              Tenant Services / Amenities
            </MotifTabControl>
          </Link>
          <Link
            to={`/client/${this.props.clientId}/bulkPeriodManagement/signOff`}
            tabIndex="-1"
          >
            <MotifTabControl
              role="tab"
              data-testid="tbSignOff"
              onClick={() => this.handleTabClick(5)}
            >
              Sign Off
            </MotifTabControl>
          </Link>
        </MotifTabNavigation>
        {noReit ? (
          <div className="mt-3">No REITs found for the selected client.</div>
        ) : (
          <TenantServiceHeader
            clientId={this.props.clientId}
            reportPeriods={this.props.periods}
            defaultReportPeriod={this.state.defaultReportPeriod}
            isActionsDisabled={this.state.isActionsDisabled}
            handleUploadPropertiesTenantServices={
              this.handleUploadPropertiesTenantServices
            }
            handleExportPropertiesTenantServices={
              this.handleExportPropertiesTenantServices
            }
            handleEditPropertiesTenantServices={
              this.handleEditPropertiesTenantServices
            }
            noDataForReportPeriod={noData}
            isEditModeActive={this.state.isEditModeActive}
          />
        )}
        {noData && !noReit ? (
          <div className="mt-5">
            No REITs found for the selected report period.
          </div>
        ) : (
          !noData &&
          !noReit && (
            <div style={{ marginTop: '20px' }}>
              <TenantServicesTable
                tenantServicesData={this.props.tenantServicesData}
                handleGridChanges={this.handleGridChanges}
                gridColumnState={this.state.gridColumnState}
                handleGridColumnStateChange={this.handleGridColumnStateChange}
                isEditModeActive={this.state.isEditModeActive}
                toggleGridEditMode={this.toggleGridEditMode}
                amenities={amenities}
                handleAddButtonClick={this.handleAddTenantService}
                handleDeleteButtonClick={this.handleDeleteTenantService}
                gridDataAfterAction={this.state.gridDataAfterAction}
                resetUpdatedRowDataTrigger={
                  this.state.resetUpdatedRowDataTrigger
                }
                trsData={this.props.trsClientReportPeriodData}
              />
            </div>
          )
        )}

        {this.state.showUploadTenantServicesModal && (
          <UploadTenantServiceModal
            show={this.state.showUploadTenantServicesModal}
            onClose={this.toggleUploadTenantServicesModal}
            reportPeriod={this.state.selectedReportPeriod}
          />
        )}
      </>
    );
  }
}

TenantServicesContainer.propTypes = {
  clientId: PropTypes.number,
  periodActions: PropTypes.object,
  trsActions: PropTypes.object,
  tenantServicesData: PropTypes.array,
  tenantServiceActions: PropTypes.object,
  tenantServiceFormValues: PropTypes.object,
};

/**
 * Maps items from state to properties of the component
 * @param {Object} state The state
 * @returns {Object} An object containing properties that the component can access
 */
function mapStateToProps(state, ownProps) {
  return {
    clientId: Number.parseInt(ownProps.router.params.clientId),
    tenantServiceFormValues: getFormValues('tenantServiceForm')(state),
    isAudit: state.client.isAudit,
    periods: state.periods,
    tenantServicesData: state.tenantServicesData,
    trsClientReportPeriodData: state.trsClientReportPeriodData,
  };
}

/**
 * Binds actions to the dispatcher
 * @param {Object} dispatch The action dispatcher
 * @returns {Object} An object containing properties that the component can access
 */
function mapDispatchToProps(dispatch) {
  return {
    tenantServiceActions: bindActionCreators(tenantServiceActions, dispatch),
    periodActions: bindActionCreators(periodActions, dispatch),
    trsActions: bindActionCreators(trsActions, dispatch),
  };
}

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(
    reduxForm({ form: 'tenantServiceForm', enableReinitialize: true })(
      TenantServicesContainer
    )
  )
);
